import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, ArrowCounterClockwise, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { downloadBlob } from "../lib/auditDownload";

// Review & edit the generated care plan before it prints or gets signed.
// The saved copy is exactly what prints — "Regenerate" throws the edits away and rebuilds from the record.
export default function CarePlanReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/clients/${id}/careplan/review`)
      .then((r) => { setData(r.data); setBlocks(r.data.blocks); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setBlock = (i, patch) => setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/clients/${id}/careplan/review`, { blocks });
      toast.success("Saved — this exact wording prints and signs from now on");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const regenerate = async () => {
    if (!window.confirm("Throw away your edits and regenerate from the client's current answers?")) return;
    setBusy("regen");
    try {
      await api.delete(`/clients/${id}/careplan/review`);
      toast.success("Regenerated from the client's answers");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  if (!data) return <div className="p-6 text-sm text-stone-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6" data-testid="careplan-review-page">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button onClick={() => navigate(`/clients/${id}`)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="cpr-back">
          <ArrowLeft size={14} /> Client
        </button>
        <span className="text-stone-300">·</span>
        <button onClick={() => navigate(`/clients/intake/${id}`)} className="text-sm font-medium text-[#6A3E97] hover:underline" data-testid="cpr-to-intake">
          Intake & Questionnaire →
        </button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Review & edit the care plan</h1>
          <p className="mt-1 max-w-xl text-sm text-stone-500">
            This is exactly what prints and what the client signs. Fix wording, soften anything the client would find
            off-putting, or remove lines that don't belong — your edits are the final copy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.overridden && (
            <button onClick={regenerate} disabled={busy === "regen"} data-testid="cpr-regenerate"
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-stone-300 px-4 text-xs font-semibold text-stone-600 transition-colors hover:bg-stone-900 hover:text-white disabled:opacity-40">
              <ArrowCounterClockwise size={13} /> {busy === "regen" ? "Regenerating…" : "Regenerate"}
            </button>
          )}
          <button onClick={() => downloadBlob(`/clients/${id}/careplan/print.pdf`, "care-plan.pdf")} data-testid="cpr-print"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-stone-300 px-4 text-xs font-semibold text-stone-600 transition-colors hover:bg-stone-900 hover:text-white">
            <Printer size={13} /> Print PDF
          </button>
          <button onClick={save} disabled={busy === "save"} data-testid="cpr-save"
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-stone-900 disabled:opacity-40">
            <FloppyDisk size={13} /> {busy === "save" ? "Saving…" : "Save wording"}
          </button>
        </div>
      </div>

      {data.overridden && (
        <p className="mt-3 rounded-lg border border-[#B7791F]/40 bg-[#B7791F]/10 px-4 py-2 text-xs text-[#7a5410]" data-testid="cpr-override-note">
          You're viewing the edited copy (saved by {data.override_by} · {String(data.override_at || "").slice(0, 10)}).
          New intake answers won't change it until you Regenerate.
        </p>
      )}

      <div className="mt-5 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
        <p className="text-center font-heading text-lg font-semibold text-stone-900">{data.title}</p>
        <p className="mt-0.5 text-center text-xs text-stone-500">{data.subtitle}</p>
        <div className="mt-5 space-y-3">
          {blocks.map((b, i) => {
            if (b.type === "h") {
              return (
                <input key={i} value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })}
                  className="mt-2 w-full rounded-lg bg-[#4A235A]/[0.06] px-3 py-2 text-sm font-bold text-[#4A235A]"
                  data-testid={`cpr-h-${i}`} />
              );
            }
            if (b.type === "p") {
              return (
                <textarea key={i} value={b.text || ""} rows={Math.min(6, Math.max(2, Math.ceil((b.text || "").length / 95)))}
                  onChange={(e) => setBlock(i, { text: e.target.value })}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-800"
                  data-testid={`cpr-p-${i}`} />
              );
            }
            if (b.type === "list") {
              return (
                <textarea key={i} value={(b.lines || []).join("\n")}
                  rows={Math.min(14, Math.max(2, (b.lines || []).length))}
                  onChange={(e) => setBlock(i, { lines: e.target.value.split("\n") })}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 font-mono text-xs text-stone-700"
                  placeholder="One line per bullet"
                  data-testid={`cpr-list-${i}`} />
              );
            }
            return (
              <div key={i} className="rounded-lg bg-stone-50 px-3 py-2 text-[10px] uppercase tracking-widest text-stone-400" data-testid={`cpr-locked-${i}`}>
                {b.type === "sig" ? "— signature block (fixed) —" : "— spacing —"}
              </div>
            );
          })}
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-stone-400">Save wording, then Print PDF — the PDF is made from your saved copy.</p>
    </div>
  );
}
